import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { isLogLevel, loggingActive } from '@shared/logger';

/**
 * Initializes the i18next instance for internationalization (i18n) in the application.
 * This configuration supports dynamic language loading, language detection, and a fallback mechanism.
 * It integrates i18next with React and sets up necessary configurations for loading translation files
 * and detecting the user's preferred language.
 */
i18n.use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        /**
         * Specifies whether to use partially bundled language files, allowing smaller language packs.
         * Set to `true` to allow partial loading of language files.
         */
        partialBundledLanguages: true,

        /**
         * List of supported languages for the application.
         * This will restrict the languages the user can select.
         */
        supportedLngs: ['en', 'de'],

        /**
         * Use language-only locale files. Browser languages commonly include regions
         * such as de-DE, while this app ships de/en JSON files.
         */
        load: 'languageOnly',

        /**
         * Configuration for language detection, with multiple strategies to detect the user's language.
         */
        detection: {
            /**
             * Prefer explicit URL overrides and then the browser language. The hardcoded
             * html lang attribute should not override the user's browser settings.
             */
            order: ['querystring', 'navigator'],

            /**
             * Query string parameter to look for in the URL to detect the language.
             * Example: `?lng=de`
             */
            lookupQuerystring: 'lng',

            /**
             * Do not persist auto-detected languages. Persisting can pin users to an old
             * language even after their browser language changes.
             */
            caches: [],

            /**
             * Normalize regional browser values like de-DE to the locale files we ship.
             */
            convertDetectedLanguage: (lng: string) => lng.split('-')[0],
        },

        /**
         * Backend configuration for loading translation files from the server.
         * The function customizes the load path based on the namespace and language.
         */
        backend: {
            /**
             * A function to construct the path for language and namespace JSON files.
             * @param {string} lng - The language code (e.g., 'en', 'de').
             * @param {string} ns - The namespace of the translation (e.g., 'translation', 'user').
             * @returns {string} The URL to load the language file from.
             */
            loadPath: (lng: string, ns: string) => {
                if (ns[0] === 'translation') {
                    return `/locales/${lng}.json`; // Load base translation files
                } else {
                    return `/locales/${ns}/${lng}.json`; // Load dynamic namespace files
                }
            },
        },

        /**
         * The fallback language in case the desired language is not available.
         * If the detected language is not supported, this language will be used.
         */
        fallbackLng: 'en',

        /**
         * The fallback namespace when a translation key is missing in the current namespace.
         * Defaults to 'translation' if no namespace is provided.
         */
        fallbackNS: 'translation',

        /**
         * Interpolation settings for variable replacements in translations.
         * `escapeValue: false` means that i18next won't escape values by default.
         */
        interpolation: {
            escapeValue: false,
        },

        /**
         * React-specific options for i18next integration.
         * `useSuspense: true` allows React Suspense to be used for loading translations asynchronously.
         */
        react: {
            useSuspense: true,
        },

        /**
         * Enables debug logging if the log level is set to 2 or 3 (configured in the logger utility).
         * This will log detailed information about the translation process.
         */
        debug: loggingActive && isLogLevel(2),
    });
